/**
 * Periodization System
 * Implements training phase detection and phase-specific programming recommendations
 */

import type { WorkoutDay, ProgramMetadata, ProgramPeriodizationModel } from './types/program';

export type PeriodizationPhaseType = 
  | 'accumulation'    // Build volume, moderate intensity (RPE 6-7)
  | 'intensification' // Build intensity, reduce volume (RPE 7-8.5)
  | 'realization'     // Peak performance, low volume (RPE 8-10)
  | 'deload'          // Recovery, low everything (RPE 5-6)
  | 'maintenance'     // Sustain current fitness
  | 'assessment'      // Testing/evaluation week
  | 'hypertrophy'     // Build muscle mass (alias for accumulation)
  | 'strength';       // Build max strength (alias for intensification)

export interface PeriodizationPhase {
  type: PeriodizationPhaseType;
  weekInPhase: number;
  totalWeeksInPhase?: number;
  focus: 'volume' | 'intensity' | 'skill' | 'recovery';
  
  // Automatic adjustments for adaptive system
  volumeMultiplier: number;    // 0.6-1.2 (multiply target sets)
  intensityMultiplier: number; // 0.7-1.1 (multiply target load)
  
  // User-facing information
  name: string;
  description: string;
  icon: string;
  color: string; // Tailwind color class
  
  // Adaptive guidance
  progressionStrategy: 'add-sets' | 'add-load' | 'add-reps' | 'maintain';
  rpeTarget: number; // Target RPE for this phase
}

/**
 * Detect periodization phase based on program structure
 * Uses implicit detection from week number and workout characteristics
 */
export function detectPeriodizationPhase(
  week: number,
  totalWeeks: number,
  workoutDay?: WorkoutDay
): PeriodizationPhase {
  
  // ===== ASSESSMENT WEEKS =====
  // Week 1, 7, 13 (or final week) - Testing/Realization
  if (workoutDay?.isAssessment || 
      workoutDay?.sessionType === 'Operator Test' ||
      workoutDay?.sessionType === '1RM Testing' ||
      workoutDay?.sessionType === 'Assessment') {
    return {
      type: 'realization',
      weekInPhase: 1,
      totalWeeksInPhase: 1,
      focus: 'skill',
      volumeMultiplier: 0.7,
      intensityMultiplier: 1.0,
      name: 'Assessment Week',
      description: 'Testing peak performance and establishing baselines',
      icon: '',
      color: 'text-purple-400',
      progressionStrategy: 'maintain',
      rpeTarget: 9.0
    };
  }
  
  // ===== DELOAD WEEKS =====
  if (workoutDay?.isDeload) {
    return {
      type: 'deload',
      weekInPhase: 1,
      totalWeeksInPhase: 1,
      focus: 'recovery',
      volumeMultiplier: 0.6,
      intensityMultiplier: 0.7,
      name: 'Deload Week',
      description: 'Active recovery to dissipate fatigue and prepare for next cycle',
      icon: '',
      color: 'text-blue-400',
      progressionStrategy: 'maintain',
      rpeTarget: 6.0
    };
  }
  
  // ===== 13-WEEK OPERATOR PROGRAM (Standard Tactical Periodization) =====
  if (totalWeeks === 13) {
    // Week 2-6: Accumulation Phase
    if (week >= 2 && week <= 6) {
      const weekInPhase = week - 1;
      const progress = weekInPhase / 5; // 0.0 to 1.0
      
      return {
        type: 'accumulation',
        weekInPhase,
        totalWeeksInPhase: 5,
        focus: 'volume',
        volumeMultiplier: 1.0 + (progress * 0.2), // 1.0 → 1.2 (progressive volume)
        intensityMultiplier: 0.85, // Keep intensity moderate
        name: 'Accumulation Phase',
        description: 'Building work capacity and volume tolerance',
        icon: '',
        color: 'text-green-400',
        progressionStrategy: 'add-sets',
        rpeTarget: 7.0
      };
    }
    
    // Week 7: Mid-cycle assessment
    if (week === 7) {
      return {
        type: 'realization',
        weekInPhase: 1,
        totalWeeksInPhase: 1,
        focus: 'skill',
        volumeMultiplier: 0.7,
        intensityMultiplier: 1.0,
        name: 'Mid-Cycle Check',
        description: 'Reassess progress and adjust training trajectory',
        icon: '',
        color: 'text-purple-400',
        progressionStrategy: 'maintain',
        rpeTarget: 9.0
      };
    }
    
    // Week 8-12: Intensification Phase
    if (week >= 8 && week <= 12) {
      const weekInPhase = week - 7;
      const progress = weekInPhase / 5; // 0.0 to 1.0
      
      return {
        type: 'intensification',
        weekInPhase,
        totalWeeksInPhase: 5,
        focus: 'intensity',
        volumeMultiplier: 0.9 - (progress * 0.15), // 0.9 → 0.75 (reduce volume)
        intensityMultiplier: 0.95 + (progress * 0.1), // 0.95 → 1.05 (increase intensity)
        name: 'Intensification Phase',
        description: 'Building peak strength with progressive overload',
        icon: '',
        color: 'text-red-400',
        progressionStrategy: 'add-load',
        rpeTarget: 8.0
      };
    }
    
    // Week 13: Final assessment
    if (week === 13) {
      return {
        type: 'realization',
        weekInPhase: 1,
        totalWeeksInPhase: 1,
        focus: 'skill',
        volumeMultiplier: 0.7,
        intensityMultiplier: 1.0,
        name: 'Final Assessment',
        description: 'Demonstrate peak performance and new capabilities',
        icon: '',
        color: 'text-yellow-400',
        progressionStrategy: 'maintain',
        rpeTarget: 9.5
      };
    }
  }
  
  // ===== 8-12 WEEK PROGRAMS (Shorter Cycles) =====
  if (totalWeeks >= 8 && totalWeeks <= 12) {
    const midPoint = Math.ceil(totalWeeks / 2);
    
    // First half: Accumulation
    if (week <= midPoint) {
      const weekInPhase = week;
      const progress = weekInPhase / midPoint;
      
      return {
        type: 'accumulation',
        weekInPhase,
        totalWeeksInPhase: midPoint,
        focus: 'volume',
        volumeMultiplier: 1.0 + (progress * 0.15),
        intensityMultiplier: 0.85,
        name: 'Volume Build Phase',
        description: 'Increasing training volume to build work capacity',
        icon: '',
        color: 'text-green-400',
        progressionStrategy: 'add-sets',
        rpeTarget: 7.0
      };
    }
    
    // Second half (except last week): Intensification
    if (week > midPoint && week < totalWeeks) {
      const weekInPhase = week - midPoint;
      const totalInPhase = totalWeeks - midPoint - 1;
      const progress = weekInPhase / totalInPhase;
      
      return {
        type: 'intensification',
        weekInPhase,
        totalWeeksInPhase: totalInPhase,
        focus: 'intensity',
        volumeMultiplier: 0.9 - (progress * 0.15),
        intensityMultiplier: 0.95 + (progress * 0.1),
        name: 'Intensity Build Phase',
        description: 'Reducing volume while increasing load',
        icon: '',
        color: 'text-red-400',
        progressionStrategy: 'add-load',
        rpeTarget: 8.0
      };
    }
    
    // Last week: Deload/Testing
    if (week === totalWeeks) {
      return {
        type: 'deload',
        weekInPhase: 1,
        totalWeeksInPhase: 1,
        focus: 'recovery',
        volumeMultiplier: 0.6,
        intensityMultiplier: 0.7,
        name: 'Recovery Week',
        description: 'Active recovery before starting next cycle',
        icon: '',
        color: 'text-blue-400',
        progressionStrategy: 'maintain',
        rpeTarget: 6.0
      };
    }
  }
  
  // ===== 4-WEEK UNDULATING PROGRAMS =====
  if (totalWeeks === 4) {
    const cycleWeek = ((week - 1) % 4) + 1;
    
    // Week 1 & 3: Volume
    if (cycleWeek === 1 || cycleWeek === 3) {
      return {
        type: 'accumulation',
        weekInPhase: cycleWeek === 1 ? 1 : 2,
        totalWeeksInPhase: 2,
        focus: 'volume',
        volumeMultiplier: 1.1,
        intensityMultiplier: 0.85,
        name: 'Volume Week',
        description: 'Higher sets, moderate intensity',
        icon: '',
        color: 'text-green-400',
        progressionStrategy: 'add-sets',
        rpeTarget: 7.0
      };
    }
    
    // Week 2: Intensity
    if (cycleWeek === 2) {
      return {
        type: 'intensification',
        weekInPhase: 1,
        totalWeeksInPhase: 1,
        focus: 'intensity',
        volumeMultiplier: 0.8,
        intensityMultiplier: 1.0,
        name: 'Intensity Week',
        description: 'Lower volume, higher load',
        icon: '',
        color: 'text-red-400',
        progressionStrategy: 'add-load',
        rpeTarget: 8.5
      };
    }
    
    // Week 4: Deload
    if (cycleWeek === 4) {
      return {
        type: 'deload',
        weekInPhase: 1,
        totalWeeksInPhase: 1,
        focus: 'recovery',
        volumeMultiplier: 0.6,
        intensityMultiplier: 0.7,
        name: 'Recovery Week',
        description: 'Reset and prepare for next cycle',
        icon: '',
        color: 'text-blue-400',
        progressionStrategy: 'maintain',
        rpeTarget: 6.0
      };
    }
  }
  
  // ===== DEFAULT: LINEAR PROGRESSION (Short Programs or Unknown) =====
  // For programs < 4 weeks or custom structures
  const cyclePosition = week % 4;
  
  if (cyclePosition === 0) { // Every 4th week
    return {
      type: 'deload',
      weekInPhase: 1,
      totalWeeksInPhase: 1,
      focus: 'recovery',
      volumeMultiplier: 0.6,
      intensityMultiplier: 0.7,
      name: 'Recovery Week',
      description: 'Planned deload for recovery',
      icon: '',
      color: 'text-blue-400',
      progressionStrategy: 'maintain',
      rpeTarget: 6.0
    };
  }
  
  return {
    type: 'accumulation',
    weekInPhase: cyclePosition,
    totalWeeksInPhase: 3,
    focus: 'volume',
    volumeMultiplier: 1.0,
    intensityMultiplier: 0.9,
    name: 'Training Week',
    description: 'Standard progressive training',
    icon: '',
    color: 'text-operator-gold',
    progressionStrategy: 'add-reps',
    rpeTarget: 7.5
  };
}

/**
 * Get periodization phase for a specific workout
 * HYBRID APPROACH: Uses explicit metadata if available, falls back to auto-detection
 */
export function getPeriodizationPhase(
  week: number,
  totalWeeks: number,
  workoutDay?: WorkoutDay,
  programMetadata?: ProgramMetadata
): PeriodizationPhase {
  // STEP 1: Try explicit periodization metadata from program
  if (programMetadata?.periodizationModel) {
    const explicitPhase = getPhaseFromMetadata(
      week,
      programMetadata.periodizationModel
    );
    
    if (explicitPhase) {
      console.log(` Using EXPLICIT periodization from program metadata`);
      return explicitPhase;
    }
  }
  
  // STEP 2: Fall back to automatic detection
  console.log(` Using AUTO-DETECTED periodization`);
  return detectPeriodizationPhase(week, totalWeeks, workoutDay);
}

/**
 * Get periodization phase from explicit program metadata
 */
function getPhaseFromMetadata(
  week: number,
  model: ProgramPeriodizationModel
): PeriodizationPhase | null {
  // Find which phase configuration this week belongs to
  const phaseConfig = model.phases.find(p => p.weeks.includes(week));
  
  if (!phaseConfig) {
    console.warn(` No explicit phase found for week ${week}, falling back to auto-detection`);
    return null;
  }
  
  const weekInPhase = phaseConfig.weeks.indexOf(week) + 1;
  const totalWeeksInPhase = phaseConfig.weeks.length;
  const progress = weekInPhase / totalWeeksInPhase; // 0.0 to 1.0
  
  // Define phase-specific settings
  const phaseSettings = {
    'accumulation': {
      focus: 'volume' as const,
      volumeMultiplier: 1.0 + (progress * 0.2), // 1.0 → 1.2
      intensityMultiplier: 0.85,
      icon: '',
      color: 'text-green-400',
      progressionStrategy: 'add-sets' as const,
      rpeTarget: 7.0
    },
    'intensification': {
      focus: 'intensity' as const,
      volumeMultiplier: 0.9 - (progress * 0.15), // 0.9 → 0.75
      intensityMultiplier: 0.95 + (progress * 0.1), // 0.95 → 1.05
      icon: '',
      color: 'text-red-400',
      progressionStrategy: 'add-load' as const,
      rpeTarget: 8.0
    },
    'realization': {
      focus: 'skill' as const,
      volumeMultiplier: 0.7,
      intensityMultiplier: 1.0,
      icon: '',
      color: 'text-purple-400',
      progressionStrategy: 'maintain' as const,
      rpeTarget: 9.0
    },
    'deload': {
      focus: 'recovery' as const,
      volumeMultiplier: 0.6,
      intensityMultiplier: 0.7,
      icon: '',
      color: 'text-blue-400',
      progressionStrategy: 'maintain' as const,
      rpeTarget: 6.0
    },
    'maintenance': {
      focus: 'volume' as const,
      volumeMultiplier: 1.0,
      intensityMultiplier: 0.9,
      icon: '',
      color: 'text-operator-gold',
      progressionStrategy: 'add-reps' as const,
      rpeTarget: 7.5
    }
  };
  
  const settings = phaseSettings[phaseConfig.type as keyof typeof phaseSettings] || phaseSettings['maintenance'];
  
  return {
    type: phaseConfig.type,
    weekInPhase,
    totalWeeksInPhase,
    name: phaseConfig.name,
    description: phaseConfig.focus,
    ...settings
  };
}

/**
 * Get user-friendly description of what the phase means
 */
export function getPhaseGuidance(phase: PeriodizationPhase): {
  whatToDo: string;
  whatToAvoid: string;
  expectedFeeling: string;
} {
  const guidance = {
    'accumulation': {
      whatToDo: 'Focus on adding sets and reps. Keep RPE moderate (6-7). Build volume gradually.',
      whatToAvoid: "Don't max out or push to failure. Avoid adding weight too quickly.",
      expectedFeeling: 'Moderate fatigue. Should feel worked but not destroyed.'
    },
    'intensification': {
      whatToDo: 'Increase load while reducing sets. Push harder (RPE 7-8.5). Focus on quality over quantity.',
      whatToAvoid: "Don't add volume. Avoid grinding reps or technical breakdown.",
      expectedFeeling: 'Higher intensity, lower volume. Each set should feel challenging.'
    },
    'realization': {
      whatToDo: 'Execute at peak capacity. Perfect technique. Test your limits.',
      whatToAvoid: "Don't try new exercises. Avoid unnecessary volume.",
      expectedFeeling: 'Nervous excitement. This is your time to perform.'
    },
    'deload': {
      whatToDo: 'Reduce both load and volume. Move well, recover actively. Light RPE (5-6).',
      whatToAvoid: "Don't try to maintain intensity. Avoid AMRAP or pushing limits.",
      expectedFeeling: 'Easy, almost too light. Trust the process - recovery builds strength.'
    },
    'maintenance': {
      whatToDo: 'Sustain current fitness. Moderate intensity and volume.',
      whatToAvoid: "Don't push for PRs every session.",
      expectedFeeling: 'Comfortable, sustainable pace.'
    }
  };
  
  return guidance[phase.type as keyof typeof guidance] || guidance['maintenance'];
}

/**
 * Check if user should deload soon based on accumulated fatigue
 */
export function shouldDeloadSoon(
  currentPhase: PeriodizationPhase,
  weeksWithoutDeload: number,
  averageRecoveryScore?: number
): {
  shouldDeload: boolean;
  urgency: 'none' | 'soon' | 'now' | 'overdue';
  reason: string;
} {
  // Already in deload
  if (currentPhase.type === 'deload') {
    return {
      shouldDeload: false,
      urgency: 'none',
      reason: 'Currently in deload week'
    };
  }
  
  // Overdue (6+ weeks without deload)
  if (weeksWithoutDeload >= 6) {
    return {
      shouldDeload: true,
      urgency: 'overdue',
      reason: `${weeksWithoutDeload} weeks without deload - fatigue accumulation risk`
    };
  }
  
  // High urgency (4-5 weeks + poor recovery)
  if (weeksWithoutDeload >= 4 && averageRecoveryScore && averageRecoveryScore < 70) {
    return {
      shouldDeload: true,
      urgency: 'now',
      reason: `${weeksWithoutDeload} weeks of training + low recovery (${averageRecoveryScore.toFixed(0)}) = deload needed`
    };
  }
  
  // Medium urgency (4-5 weeks)
  if (weeksWithoutDeload >= 4) {
    return {
      shouldDeload: true,
      urgency: 'soon',
      reason: `${weeksWithoutDeload} weeks of accumulated training stress`
    };
  }
  
  // Low urgency (3 weeks + very poor recovery)
  if (weeksWithoutDeload >= 3 && averageRecoveryScore && averageRecoveryScore < 60) {
    return {
      shouldDeload: true,
      urgency: 'soon',
      reason: `Poor recovery score (${averageRecoveryScore.toFixed(0)}) - consider early deload`
    };
  }
  
  return {
    shouldDeload: false,
    urgency: 'none',
    reason: 'Training load manageable'
  };
}

